import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X, Check, LayoutGrid, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { cardsAPI, studyAPI } from '../../services/api';
import CommentSection from '../CommentSection/CommentSection';
import RichContent, { isHtml } from '../RichText/RichContent';
import ImportantTermText from '../ImportantTermText/ImportantTermText';
import logoImg from '../../assets/logo.png';
import templeImg from '../../assets/temple-far1.jpg';
import './CardBrowser.css';


function useDebounce(val, ms) {
  const [dv, setDv] = useState(val);
  useEffect(() => {
    const t = setTimeout(() => setDv(val), ms);
    return () => clearTimeout(t);
  }, [val, ms]);
  return dv;
}

function splitIntroClosing(text) {
  if (!text) return { intro: '', closing: '' };
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length <= 1) return { intro: text, closing: '' };
  return { intro: lines[0], closing: lines.slice(1).join('\n') };
}

/* Card fields can be strings, arrays or small objects: always turn them into text before rendering */
const toText = v => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(toText).filter(Boolean).join('\n');
  if (typeof v === 'object') return toText(v.text ?? v.value ?? v.label ?? v.html ?? v.content ?? v.option ?? '');
  return '';
};

/* Keeps one bad card from blanking the whole page */
class Boundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error) { console.error('Bookmarks render error:', error); }
  componentDidUpdate(prev) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.quiet) return null;
    const msg = String(error.message || error);
    if (this.props.full) {
      return (
        <div className="cb" style={{ gridTemplateColumns: '1fr', placeItems: 'center' }}>
          <p>Something went wrong. {msg}</p>
        </div>
      );
    }
    return <p className="cb-state cb-muted">Could not display this card. {msg}</p>;
  }
}

/* The stored answer may be an index, a letter or the option text */
function correctIndex(c) {
  const options = Array.isArray(c.options) ? c.options : [];
  const markedCorrect = options.findIndex(option => (
    option && typeof option === 'object' && option.isCorrect === true
  ));
  if (markedCorrect !== -1) return markedCorrect;

  const v = c.correctAnswer ?? c.correctOption ?? c.answerIndex;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const t = v.trim();
    if (/^\d+$/.test(t)) return parseInt(t, 10);
    if (/^[A-Da-d]$/.test(t)) return t.toUpperCase().charCodeAt(0) - 65;
    return options.map(toText).findIndex(o => o.trim() === t);
  }
  return -1;
}

/* ── Table parsing: pipe tables, tab tables and "List I / List II" text become real tables ── */
const SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const LIST1 = /^\W*list\s*[-–—]?\s*(?:i|1)\b/i;
const LIST2 = /list\s*[-–—]?\s*(?:ii|2)\b/i;
const LEFT = /^\(?([A-Ea-e])[.)]\)?\s+(.+)$/;
const RIGHT = /^\(?([1-9])[.)]\)?\s+(.+)$/;
const cells = line => line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.trim());

/* Finds a table starting at lines[i]: pipe tables, tab tables and "List I / List II" text */
function tryTable(lines, i) {
  const line = lines[i].trim();
  const next = (lines[i + 1] || '').trim();

  if (line.includes('|') && cells(line).length >= 2) {
    if (SEP.test(next)) {
      const rows = [];
      let j = i + 2;
      while (j < lines.length && lines[j].includes('|')) { rows.push(cells(lines[j])); j += 1; }
      return { head: cells(line), rows, next: j };
    }
    if (next.includes('|') && cells(next).length >= 2) {
      const all = [];
      let j = i;
      while (j < lines.length && lines[j].includes('|') && !SEP.test(lines[j].trim())) { all.push(cells(lines[j])); j += 1; }
      if (all.length >= 2) return { head: all[0], rows: all.slice(1), next: j };
    }
  }

  if (line.includes('\t') && next.includes('\t')) {
    const all = [];
    let j = i;
    while (j < lines.length && lines[j].includes('\t')) { all.push(lines[j].trim().split(/\t+/)); j += 1; }
    return { head: all[0], rows: all.slice(1), next: j };
  }

  if (LIST1.test(line)) {
    if (LIST2.test(line)) {
      const head = [line.slice(0, line.search(LIST2)).trim(), line.slice(line.search(LIST2)).trim()];
      const rows = [];
      const lefts = [];
      const rights = [];
      let j = i + 1;
      while (j < lines.length) {
        const t = lines[j].trim();
        if (!t) break;
        const parts = t.split(/\t+|\s{2,}/);
        const both = t.match(/^\(?([A-Ea-e])[.)]\)?\s+(.+?)\s+\(?([1-9])[.)]\)?\s+(.+)$/);
        if (parts.length >= 2 && LEFT.test(parts[0]) && RIGHT.test(parts[1])) rows.push([parts[0], parts[1]]);
        else if (both) rows.push([`${both[1]}. ${both[2]}`, `${both[3]}. ${both[4]}`]);
        else if (LEFT.test(t) && !rights.length) lefts.push(t);
        else if (RIGHT.test(t)) rights.push(t);
        else break;
        j += 1;
      }
      for (let r = 0; r < Math.max(lefts.length, rights.length); r += 1) rows.push([lefts[r] || '', rights[r] || '']);
      if (rows.length) return { head, rows, next: j };
    } else {
      const lefts = [];
      let j = i + 1;
      while (j < lines.length && LEFT.test(lines[j].trim())) { lefts.push(lines[j].trim()); j += 1; }
      if (lefts.length && j < lines.length && LIST2.test(lines[j].trim())) {
        const head = [line, lines[j].trim()];
        const rights = [];
        j += 1;
        while (j < lines.length && RIGHT.test(lines[j].trim())) { rights.push(lines[j].trim()); j += 1; }
        if (rights.length) return { head, rows: lefts.map((l, r) => [l, rights[r] || '']), next: j };
      }
    }
  }
  return null;
}

function TableBlock({ t }) {
  return (
    <div className="cb-match">
      <table>
        <thead><tr>{t.head.map((h, i) => <th key={i}><ImportantTermText text={h} /></th>)}</tr></thead>
        <tbody>{t.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}><ImportantTermText text={c} /></td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function FormattedText({ text }) {
  if (!text) return null;
  if (isHtml(text)) return <div className="cb-rich"><RichContent html={text} /></div>;
  const lines = text.split('\n');
  const out = [];
  for (let i = 0; i < lines.length;) {
    const t = lines[i].trim();
    if (!t) { out.push(<div key={i} className="cb-gap" />); i += 1; continue; }
    const tb = tryTable(lines, i);
    if (tb) { out.push(<TableBlock key={i} t={tb} />); i = tb.next; continue; }
    const num = t.match(/^(\d+)[.)]\s/);
    const bullet = /^[•\-*→]\s/.test(t);
    const clean = t.replace(/^\d+[.)]\s*|^[•\-*→]\s*|\*\*/g, '').trim();
    if (num) out.push(<div key={i} className="cb-li"><b>{num[1]}.</b><span><ImportantTermText text={clean} /></span></div>);
    else if (bullet) out.push(<div key={i} className="cb-li"><b>•</b><span><ImportantTermText text={clean} /></span></div>);
    else out.push(<p key={i}><ImportantTermText text={t} /></p>);
    i += 1;
  }
  return <div className="cb-text">{out}</div>;
}

function FormattedQuestion({ text }) {
  if (!text) return null;
  if (isHtml(text)) return <div className="cb-qtext cb-rich"><RichContent html={text} /></div>;
  const lines = text.split('\n');
  const out = [];
  for (let i = 0; i < lines.length;) {
    const t = lines[i].trim();
    if (!t) { i += 1; continue; }
    const tb = tryTable(lines, i);
    if (tb) { out.push(<TableBlock key={i} t={tb} />); i = tb.next; continue; }
    const num = t.match(/^(\d+)[.)]\s/);
    const clean = t.replace(/^\d+[.)]\s*|\*\*/g, '').trim();
    if (num) out.push(<div key={i} className="cb-li"><b>{num[1]}.</b><span><ImportantTermText text={clean} /></span></div>);
    else out.push(<p key={i} className={/^[A-Z][^a-z]{4,}/.test(t) ? 'cb-strong' : ''}><ImportantTermText text={t.replace(/\*\*/g, '')} /></p>);
    i += 1;
  }
  return <div className="cb-qtext">{out}</div>;
}


function BookmarksInner() {
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem('vidyacards_token');

  const [items, setItems] = useState([]);          // [{ card, progress }]
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [filters, setFilters] = useState({ subject: '', examType: '', difficulty: '', search: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [picked, setPicked] = useState(null);
  const [revealedCorrect, setRevealedCorrect] = useState(null);
  const [checkingAnswer, setCheckingAnswer] = useState(false);
  const [answerError, setAnswerError] = useState('');
  const [navOpen, setNavOpen] = useState(false);
  const contentRef = useRef(null);
  const answerRequestRef = useRef(0);
  const search = useDebounce(filters.search, 250);

  useEffect(() => {
    studyAPI.getBookmarks()
      .then(res => setItems((res.data?.cards || []).filter(i => i && i.card && i.card._id)))
      .catch(err => { console.error(err); setFailed(true); })
      .finally(() => setLoading(false));
  }, []);

  const statusById = useMemo(() => new Map(items.map(i => [i.card._id, toText(i.progress?.status)])), [items]);

  /* Search and filters run on the saved cards */
  const cards = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.map(i => i.card).filter(c =>
      (!filters.subject || toText(c.subject) === filters.subject) &&
      (!filters.examType || toText(c.examType) === filters.examType) &&
      (!filters.difficulty || toText(c.difficulty) === filters.difficulty) &&
      (!q || [c.question, c.subject, c.answer, c.explanation, ...(Array.isArray(c.options) ? c.options : [])]
        .some(v => toText(v).toLowerCase().includes(q)))
    );
  }, [items, filters.subject, filters.examType, filters.difficulty, search]);

  const subjectOptions = useMemo(
    () => [...new Set(items.map(i => toText(i.card.subject).trim()).filter(Boolean))].sort(),
    [items]
  );

  /* Full-screen page: lock the page behind it */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = [html.style.overflow, body.style.overflow];
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => { html.style.overflow = prev[0]; body.style.overflow = prev[1]; };
  }, []);

  /* Panel groups follow the chosen filters, one group per subject */
  const { flat, groups } = useMemo(() => {
    const by = new Map();
    cards.forEach(c => {
      const s = toText(c.subject).trim() || 'General';
      if (!by.has(s)) by.set(s, []);
      by.get(s).push(c);
    });
    const flatList = [];
    const groupList = [];
    by.forEach((items, subject) => {
      groupList.push({ subject, ids: items.map(c => { flatList.push(c); return c._id; }) });
    });
    return { flat: flatList, groups: groupList };
  }, [cards]);

  useEffect(() => {
    if (flat.length && !flat.some(c => c._id === selectedId)) setSelectedId(flat[0]._id);
    if (!flat.length) setSelectedId(null);
  }, [flat, selectedId]);

  const idx = flat.findIndex(c => c._id === selectedId);
  const card = idx > -1 ? flat[idx] : null;

  useEffect(() => {
    answerRequestRef.current += 1;
    setPicked(null);
    setRevealedCorrect(null);
    setCheckingAnswer(false);
    setAnswerError('');
    contentRef.current?.scrollTo?.(0, 0);
  }, [selectedId]);

  const goTo = id => { setSelectedId(id); setNavOpen(false); };
  const goPrev = useCallback(() => { if (idx > 0) setSelectedId(flat[idx - 1]._id); }, [idx, flat]);
  const goNext = useCallback(() => { if (idx > -1 && idx < flat.length - 1) setSelectedId(flat[idx + 1]._id); }, [idx, flat]);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') { setNavOpen(false); return; }
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev]);

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));
  const hasFilters = !!(filters.subject || filters.examType || filters.difficulty || filters.search);
  const clearFilters = () => setFilters({ subject: '', examType: '', difficulty: '', search: '' });

  /* Card details */
  const options = (Array.isArray(card?.options) ? card.options : []).map(toText);
  const isMcq = options.length > 0;
  const correct = revealedCorrect ?? (card ? correctIndex(card) : -1);
  const done = picked !== null;
  const side = !card ? '' : toText(isMcq ? (card.explanation || card.answer) : (card.modelAnswer || card.answer || card.explanation));
  const showSide = !!side && (!isMcq || done);
  const stmts = (Array.isArray(card?.statements) ? card.statements : []).map(toText);
  const { intro, closing } = stmts.length ? splitIntroClosing(toText(card.question)) : { intro: '', closing: '' };
  const long = options.some(o => String(o).length > 48);

  const pickOption = async optionIndex => {
    if (!card || done || checkingAnswer) return;

    const cardId = card._id;
    const requestId = ++answerRequestRef.current;
    setAnswerError('');
    const knownCorrect = correctIndex(card);
    if (knownCorrect >= 0) {
      setRevealedCorrect(knownCorrect);
      setPicked(optionIndex);
      return;
    }

    setCheckingAnswer(true);
    try {
      const response = await cardsAPI.getOne(cardId);
      const answerIndex = correctIndex(response.data?.card || {});
      if (answerIndex < 0) throw new Error('The correct answer is unavailable for this question.');
      if (requestId !== answerRequestRef.current) return;
      setRevealedCorrect(answerIndex);
      setPicked(optionIndex);
    } catch (error) {
      if (requestId !== answerRequestRef.current) return;
      setAnswerError(error.response?.data?.message || error.message || 'Could not check your answer. Please try again.');
    } finally {
      if (requestId === answerRequestRef.current) setCheckingAnswer(false);
    }
  };

  return (
    <div className="cb">
      {/* Panel: fixed, only the question list inside it scrolls */}
      <aside className={`cb-side ${navOpen ? 'open' : ''}`} aria-label="Questions">
        <img className="cb-side-art" src={templeImg} alt="" />
        <div className="cb-side-body">
          <div className="cb-side-top">
            <Link to="/dashboard" className="cb-logo" aria-label="CivilFox home"><img src={logoImg} alt="" /></Link>
            <button type="button" className="cb-x" onClick={() => setNavOpen(false)} aria-label="Close"><X size={18} /></button>
          </div>

          <nav className="cb-subjects">
            {groups.map(g => (
              <section key={g.subject} className={g.ids.includes(selectedId) ? 'on' : ''}>
                <h2><i className="cb-dot" />{g.subject}</h2>
                <div className="cb-tiles">
                  {g.ids.map((id, n) => (
                    <button key={id} type="button" className={`cb-tile ${id === selectedId ? 'cur' : ''}`}
                      aria-label={`${g.subject} ${n + 1}`} onClick={() => goTo(id)}>{n + 1}</button>
                  ))}
                </div>
              </section>
            ))}
          </nav>

          <button type="button" className="cb-back" onClick={() => navigate(-1)}><ArrowLeft size={18} />Go back</button>
        </div>
      </aside>

      {/* Right: search, filters, question */}
      <div className="cb-content" ref={contentRef}>
        <div className="cb-bar">
          <button type="button" className="cb-grid-btn" onClick={() => setNavOpen(true)} aria-label="All questions">
            <LayoutGrid size={18} />
          </button>
          <label className="cb-search">
            <Search size={20} />
            <input type="text" enterKeyHint="search" placeholder="Search" aria-label="Search questions"
              autoComplete="off" value={filters.search} onChange={e => setFilter('search', e.target.value)} />
          </label>
        </div>

        <div className="cb-filters">
          <select value={filters.subject} onChange={e => setFilter('subject', e.target.value)} aria-label="Subject">
            <option value="">All subjects</option>
            {subjectOptions.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filters.examType} onChange={e => setFilter('examType', e.target.value)} aria-label="Type">
            <option value="">All types</option>
            <option value="Prelims">Prelims</option>
            <option value="Mains">Mains</option>
          </select>
          <select value={filters.difficulty} onChange={e => setFilter('difficulty', e.target.value)} aria-label="Difficulty">
            <option value="">All levels</option>
            <option>Easy</option>
            <option>Medium</option>
            <option>Hard</option>
          </select>
          {hasFilters && (
            <button type="button" className="cb-clear" onClick={clearFilters} aria-label="Clear filters"><X size={16} /></button>
          )}
        </div>

        {loading ? (
          <div className="cb-state"><span className="cb-spin" role="status" aria-label="Loading" /></div>
        ) : !card ? (
          <p className="cb-state cb-muted">{items.length ? 'No cards match.' : failed ? 'Could not load bookmarks.' : 'No bookmarks yet.'}</p>
        ) : (
          <Boundary resetKey={selectedId}>
          <article>
            <section className="cb-card">
              <div className="cb-tags">
                {toText(card.subject) && <span>{toText(card.subject)}</span>}
                {toText(card.examType) && <span>{toText(card.examType)}</span>}
                {toText(card.difficulty) && <span>{toText(card.difficulty)}</span>}
                {statusById.get(card._id) && <span>{statusById.get(card._id)}</span>}
              </div>

              <div className="cb-q">
                {stmts.length ? (
                  <>
                    <FormattedQuestion text={intro} />
                    <ol className="cb-stmts">{stmts.map((s, i) => <li key={i}>{s}</li>)}</ol>
                    {closing && <FormattedQuestion text={closing} />}
                  </>
                ) : <FormattedQuestion text={toText(card.question)} />}
              </div>

              {isMcq && (
                <div className={`cb-opts ${long ? 'list' : ''}`} role="group" aria-label="Options">
                  {options.map((opt, i) => {
                    let st = '';
                    if (done) st = i === correct ? 'ok' : i === picked ? 'bad' : 'dim';
                    return (
                      <button key={i} type="button" className={`cb-opt ${st} ${done || checkingAnswer ? 'locked' : ''}`}
                        disabled={done || checkingAnswer}
                        aria-pressed={i === picked} onClick={() => pickOption(i)}>
                        <b>{String.fromCharCode(65 + i)}</b>
                        <span>{opt}</span>
                        {st === 'ok' && <Check size={17} role="img" aria-label="Correct" />}
                        {st === 'bad' && <X size={17} role="img" aria-label="Wrong" />}
                      </button>
                    );
                  })}
                </div>
              )}
              {(checkingAnswer || answerError) && (
                <p className="cb-answer-feedback" role={answerError ? 'alert' : 'status'}>
                  {checkingAnswer ? 'Checking answer…' : answerError}
                </p>
              )}
            </section>

            {/* Answer sits outside the box, like a Wikipedia page */}
            {showSide && <div className="cb-a"><FormattedText text={side} /></div>}

            {flat.length > 1 && (
              <div className="cb-nav">
                <button type="button" className="cb-nbtn" onClick={goPrev} disabled={idx <= 0} aria-label="Previous"><ChevronLeft size={18} /></button>
                <span className="cb-pos">{idx + 1}/{flat.length}</span>
                <button type="button" className="cb-nbtn" onClick={goNext} disabled={idx >= flat.length - 1} aria-label="Next"><ChevronRight size={18} /></button>
              </div>
            )}

            <div className="cb-comments"><Boundary resetKey={selectedId} quiet><CommentSection cardId={card._id} isLoggedIn={isLoggedIn} /></Boundary></div>
          </article>
          </Boundary>
        )}
      </div>

      <div className={`cb-backdrop ${navOpen ? 'show' : ''}`} onClick={() => setNavOpen(false)} />
    </div>
  );
}

export default function Bookmarks() {
  return <Boundary full><BookmarksInner /></Boundary>;
}